import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Telescope, ArrowRight, X } from "lucide-react";
import { getNextSkyEventTeaser } from "@/lib/api";
import useSubscription from "@/hooks/useSubscription";

const DISMISS_KEY = "ep_next_sky_chip_dismissed_v1";

/**
 * NextSkyEventChip — a tease chip on the dashboard shown ONLY to
 * non-subscribers, revealing the very next big sky event (new/full moon,
 * meteor shower, comet, eclipse, equinox) with a countdown, plus a soft
 * nudge to /subscribe for the full 90-day feed.
 *
 * Dismissable — dismissal is remembered in localStorage for 24 hours so
 * it doesn't nag every load.
 */
export default function NextSkyEventChip() {
  const { active: subActive, loading: subLoading } = useSubscription();
  const [event, setEvent] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  // Load dismissal state (expires after 24h)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (!raw) return;
      const ts = parseInt(raw, 10);
      if (Number.isFinite(ts) && Date.now() - ts < 24 * 3600 * 1000) {
        setDismissed(true);
      }
    } catch { /* ignore */ }
  }, []);

  // Fetch the next event (only for non-subscribers)
  useEffect(() => {
    if (subLoading || subActive || dismissed) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await getNextSkyEventTeaser();
        if (!cancelled) setEvent(r?.event || null);
      } catch {
        if (!cancelled) setEvent(null);
      }
    })();
    return () => { cancelled = true; };
  }, [subLoading, subActive, dismissed]);

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* */ }
    setDismissed(true);
  };

  if (subActive || subLoading || dismissed || !event) return null;

  const when = new Date(event.start);
  const dayLabel = when.toLocaleDateString("en-GB", { day: "numeric", month: "long" });
  const away =
    event.days_away === 0 ? "tonight" :
    event.days_away === 1 ? "in 1 day" :
    `in ${event.days_away} days`;

  return (
    <div className="mb-8 stagger-in" data-testid="next-sky-event-chip">
      <Link
        to="/subscribe"
        className="group relative flex items-center gap-3 md:gap-4 px-4 md:px-5 py-3 md:py-3.5 rounded-full border border-[color:var(--gold)]/30 hover:border-[color:var(--gold)]/60 bg-[color:var(--gold)]/[0.04] hover:bg-[color:var(--gold)]/[0.08] transition-all no-underline"
        data-testid="next-sky-event-chip-link"
        style={{ boxShadow: "0 8px 32px -12px rgba(212, 162, 76, 0.15)" }}
      >
        <div className="shrink-0 w-9 h-9 rounded-full border border-[color:var(--gold)]/40 flex items-center justify-center bg-[color:var(--bg-base)]">
          <Telescope size={14} className="text-[color:var(--gold)]" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="label-overline text-[10px] text-[color:var(--gold)] whitespace-nowrap"
              data-testid="next-sky-event-chip-countdown"
            >
              your next sky event · {away}
            </span>
            <span className="font-serif-italic text-[color:var(--text-muted)] text-xs hidden sm:inline">
              · {dayLabel}
            </span>
          </div>
          <div
            className="mt-0.5 font-serif-display text-[color:var(--parchment)] text-base md:text-lg tracking-tight truncate"
            data-testid="next-sky-event-chip-title"
          >
            {event.title}
          </div>
        </div>

        <span
          className="hidden sm:inline-flex items-center gap-1.5 shrink-0 label-overline text-[10px] text-[color:var(--gold)] group-hover:text-[color:var(--parchment)] transition"
        >
          unlock the feed <ArrowRight size={11} className="group-hover:translate-x-0.5 transition-transform" />
        </span>

        <button
          type="button"
          onClick={handleDismiss}
          className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[color:var(--text-muted)] hover:text-[color:var(--parchment)] hover:bg-[color:var(--gold)]/10 transition"
          aria-label="Dismiss"
          data-testid="next-sky-event-chip-dismiss"
        >
          <X size={13} />
        </button>
      </Link>
    </div>
  );
}
